import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GitCompare, Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { EmptyState, PageHeader, ToneChip } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const SEV_TONE = { critico: "danger", alto: "warning", medio: "info", bajo: "muted" };
const DIFF_TYPE = { anadido: "Añadido", eliminado: "Eliminado", modificado: "Modificado" };

function NewComparisonDialog({ open, onClose, onDone }) {
  const { data: contracts } = useQuery({ queryKey: ["contracts-mini"], queryFn: async () => (await api.get("/legal/contracts")).data });
  const { data: templates } = useQuery({ queryKey: ["contract-templates"], queryFn: async () => (await api.get("/legal/templates")).data });
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  const run = async () => {
    try {
      const r = await api.post("/legal/comparisons/run", {
        a_contract_id: a,
        b_model_id: b.startsWith("mtpl") ? b : undefined,
        b_contract_id: b.startsWith("mtpl") ? undefined : b,
      });
      toast.success("Comparación ejecutada y guardada (reproducible)");
      onDone(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-comparison-dialog">
        <DialogHeader><DialogTitle>Nueva comparación</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Documento A (contrato actual)</Label>
            <Select value={a} onValueChange={setA}>
              <SelectTrigger data-testid="comparison-a"><SelectValue placeholder="Seleccionar contrato…" /></SelectTrigger>
              <SelectContent>{(contracts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.contract_number} — {c.title.slice(0, 40)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Documento B (versión anterior, precedente, modelo, oferta o adenda)</Label>
            <Select value={b} onValueChange={setB}>
              <SelectTrigger data-testid="comparison-b"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
              <SelectContent>
                {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>Modelo: {t.name}</SelectItem>)}
                {(contracts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.contract_number} — {c.title.slice(0, 40)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" disabled={!a || !b || a === b} onClick={run} data-testid="run-comparison-submit"><GitCompare className="mr-1 h-4 w-4" /> Ejecutar comparación</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ComparisonDetail({ id }) {
  const { map } = useUsers();
  const qc = useQueryClient();
  const [catFilter, setCatFilter] = useState("");
  const [sevFilter, setSevFilter] = useState("");
  const { data: comp, isLoading, refetch } = useQuery({
    queryKey: ["comparison", id],
    queryFn: async () => (await api.get(`/legal/comparisons/${id}`)).data,
  });

  const act = async (idx, action) => {
    try {
      const r = await api.post(`/legal/comparisons/${id}/difference/${idx}/action`, { action });
      if (action === "proponer_redaccion") toast.success("Propuesta IA generada — REQUIERE REVISIÓN LEGAL");
      else toast.success("Diferencia actualizada");
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading || !comp) return <p className="py-10 text-center text-sm text-slate-500">Cargando comparación…</p>;

  const diffs = comp.differences.filter((d) => (!catFilter || d.category === catFilter) && (!sevFilter || d.severity === sevFilter));
  const cats = [...new Set(comp.differences.map((d) => d.category))];

  return (
    <div data-testid="comparison-detail">
      <div className="ts-card mb-4 p-4">
        <p className="ts-caption">Resumen ejecutivo</p>
        <p className="mt-1 text-sm text-slate-800">{comp.summary}</p>
        <p className="mt-1 text-xs text-slate-500">
          A: <strong>{comp.a_label}</strong> vs B: <strong>{comp.b_label}</strong> · {comp.kind} · por {userName(map, comp.created_by)} · {fmtDateTime(comp.created_at)}
        </p>
        <div className="mt-3 flex gap-2">
          <Select value={catFilter} onValueChange={(v) => setCatFilter(v === "todas" ? "" : v)}>
            <SelectTrigger className="h-8 w-48" data-testid="diff-cat-filter"><SelectValue placeholder="Categoría" /></SelectTrigger>
            <SelectContent><SelectItem value="todas">Todas las categorías</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sevFilter} onValueChange={(v) => setSevFilter(v === "todas" ? "" : v)}>
            <SelectTrigger className="h-8 w-36" data-testid="diff-sev-filter"><SelectValue placeholder="Severidad" /></SelectTrigger>
            <SelectContent><SelectItem value="todas">Todas</SelectItem>{["critico", "alto", "medio", "bajo"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        {diffs.map((d, i) => {
          const idx = comp.differences.indexOf(d);
          return (
            <div key={i} className="ts-card p-4" data-testid={`diff-${idx}`}>
              <div className="flex flex-wrap items-center gap-2">
                <ToneChip label={DIFF_TYPE[d.type]} tone={d.type === "eliminado" ? "danger" : d.type === "anadido" ? "info" : "warning"} />
                <ToneChip label={d.severity} tone={SEV_TONE[d.severity]} />
                <p className="text-sm font-semibold capitalize text-slate-900">{d.key.replace(/_/g, " ")}</p>
                <span className="text-xs text-slate-400">{d.clause}</span>
                <ToneChip label={d.status} tone={d.status === "confirmada" ? "success" : d.status === "irrelevante" ? "muted" : "warning"} />
              </div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <div className="rounded-sm border border-red-100 bg-red-50/50 p-2">
                  <p className="text-[10px] font-semibold uppercase text-red-400">A — {comp.a_label.slice(0, 30)}</p>
                  <p className="mt-0.5 text-xs text-slate-700">{d.a ?? "— (ausente)"}</p>
                </div>
                <div className="rounded-sm border border-emerald-100 bg-emerald-50/50 p-2">
                  <p className="text-[10px] font-semibold uppercase text-emerald-500">B — {comp.b_label.slice(0, 30)}</p>
                  <p className="mt-0.5 text-xs text-slate-700">{d.b ?? "— (ausente)"}</p>
                </div>
              </div>
              {d.proposal && (
                <div className="mt-2 rounded-sm border border-violet-200 bg-violet-50 p-3" data-testid={`diff-proposal-${idx}`}>
                  <p className="text-[10px] font-bold uppercase text-violet-600">{d.proposal.marked}</p>
                  <p className="mt-1 text-xs text-slate-800"><strong>Texto propuesto:</strong> {d.proposal.text}</p>
                  <p className="mt-1 text-xs text-slate-600">{d.proposal.explanation}</p>
                  <p className="mt-0.5 text-xs text-slate-500">Riesgos residuales: {d.proposal.residual_risks} · Modelo: {d.proposal.model_used}</p>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(idx, "confirmar")} data-testid={`diff-confirm-${idx}`}>Confirmar diferencia</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => act(idx, "irrelevante")} data-testid={`diff-irrelevant-${idx}`}>Marcar irrelevante</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(idx, "crear_riesgo")} data-testid={`diff-risk-${idx}`}>Crear riesgo</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(idx, "crear_tarea")} data-testid={`diff-task-${idx}`}>Crear tarea</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-violet-700" onClick={() => act(idx, "proponer_redaccion")} data-testid={`diff-redraft-${idx}`}>Proponer redacción (IA)</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Comparaciones() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { map } = useUsers();
  const [newOpen, setNewOpen] = useState(false);
  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["comparisons"],
    queryFn: async () => (await api.get("/legal/comparisons")).data,
  });

  return (
    <div data-testid="comparaciones-page">
      <PageHeader
        title="Comparaciones"
        subtitle="Contrato vs versión anterior, precedente, modelo corporativo, oferta o adenda. Toda comparación queda guardada y es reproducible."
        actions={<Button onClick={() => setNewOpen(true)} data-testid="new-comparison-button"><Plus className="mr-1 h-4 w-4" /> Nueva comparación</Button>}
      />
      {id ? (
        <>
          <Button variant="ghost" size="sm" className="mb-3" onClick={() => navigate("/legal/comparaciones")} data-testid="back-to-comparisons">← Todas las comparaciones</Button>
          <ComparisonDetail id={id} />
        </>
      ) : isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin comparaciones" description="Ejecuta la primera comparación entre un contrato y su modelo o versión anterior." />
      ) : (
        <div className="space-y-2">
          {items.map((cmp) => (
            <button key={cmp.id} onClick={() => navigate(`/legal/comparaciones/${cmp.id}`)} className="ts-card ts-card-hover block w-full p-4 text-left" data-testid={`comparison-${cmp.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <GitCompare className="h-4 w-4 text-slate-400" />
                <p className="text-sm font-medium text-slate-900">{cmp.a_label} <span className="text-slate-400">vs</span> {cmp.b_label}</p>
                <ToneChip label={cmp.kind.replace(/_/g, " ")} tone="info" />
              </div>
              <p className="mt-1 text-xs text-slate-500">{cmp.summary} · {userName(map, cmp.created_by)} · {fmtDateTime(cmp.created_at)}</p>
            </button>
          ))}
        </div>
      )}
      <NewComparisonDialog open={newOpen} onClose={() => setNewOpen(false)} onDone={(cmp) => { setNewOpen(false); refetch(); navigate(`/legal/comparaciones/${cmp.id}`); }} />
    </div>
  );
}
